import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MuiPhoneNumber from "material-ui-phone-number";
import { marketingFonts } from "../../app/components/tickitMarketingTheme";
import { getBrandingColors, brandedFieldSx, brandedTextSx, brandedPhoneFieldSx, isCategoryAvailable } from "./brandingUtils";
import { layout, sectionBorderSx } from "./templateLayout";

export default function RegistrationForm({
  event,
  user,
  branding = {},
  onSubmit,
  submitting = false,
  embedded = false,
}) {
  const [category, setCategory] = React.useState("");
  const [categoryError, setCategoryError] = React.useState("");
  const [forMe, setForMe] = React.useState({ 0: Boolean(user) });
  const [total, setTotal] = React.useState(0);

  const { primary: primaryColor, accent: accentColor } = getBrandingColors(branding);
  const fieldSx = {
    ...brandedFieldSx(accentColor),
    width: "100%",
    "& .MuiInputBase-root": { minHeight: 48 },
  };
  const phoneInputSx = brandedPhoneFieldSx(accentColor);
  const headingSx = {
    fontWeight: 700,
    mb: { xs: 1.5, sm: 2 },
    fontSize: { xs: "1rem", sm: "1.1rem" },
    fontFamily: marketingFonts.display,
    ...brandedTextSx(accentColor),
  };

  const handleCategoryChange = (e) => {
    const val = e.target.value;
    setCategory(val);
    setCategoryError("");
    const cat = event?.categories?.find((c) => c.category === val);
    setTotal(cat?.price || 0);
  };

  const handleForMe = (e) => {
    const { name, checked } = e.target;
    setForMe({ ...forMe, [name]: checked });
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!category) {
      setCategoryError("Please select a category");
      return;
    }
    const data = new FormData(ev.currentTarget);
    const userInfo = {
      userId: forMe[0] ? user?._id : undefined,
      name: forMe[0] ? user?.name : data.get("ticket-0-name"),
      email: forMe[0] ? user?.email : data.get("ticket-0-email"),
      phoneNumber: forMe[0] ? user?.phoneNumber : data.get("ticket-0-mob"),
    };
    event?.moreInfo?.forEach((info) => {
      userInfo[info] = data.get(`ticket-0-${info}`);
    });
    const cat = event.categories.find((c) => c.category === category);
    onSubmit({
      userInfo,
      category,
      price: cat?.price || 0,
    });
  };

  const sectionSx = embedded
    ? { p: 0, bgcolor: "transparent", boxShadow: "none", borderRadius: 0 }
    : {
        p: { xs: 2, sm: 2.5, md: 3 },
        borderRadius: 2,
        ...sectionBorderSx(branding),
        bgcolor: "#fff",
      };

  const Section = ({ title, children }) => (
    <Paper elevation={0} sx={sectionSx}>
      <Typography component="h2" sx={headingSx}>
        {title}
      </Typography>
      {children}
    </Paper>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      <Stack spacing={layout.sectionGap}>
        <Section title="Select category">
          <FormControl fullWidth required error={Boolean(categoryError)}>
            <Select
              value={category}
              onChange={handleCategoryChange}
              displayEmpty
              sx={fieldSx}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: "50vh",
                    "& .MuiMenuItem-root": { color: accentColor, minHeight: 44 },
                  },
                },
              }}
            >
              <MenuItem value="" disabled>
                Select category
              </MenuItem>
              {event?.categories?.map(
                (cat) =>
                  isCategoryAvailable(cat) && (
                    <MenuItem key={cat.category} value={cat.category}>
                      {cat.category} — {cat.price} EGP
                    </MenuItem>
                  )
              )}
            </Select>
            {categoryError && (
              <FormHelperText sx={{ color: accentColor }}>{categoryError}</FormHelperText>
            )}
          </FormControl>
        </Section>

        <Section title="Your details">
          <Stack spacing={2}>
            {user && (
              <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                <Checkbox
                  name="0"
                  checked={forMe[0]}
                  onChange={handleForMe}
                  sx={{
                    color: accentColor,
                    "&.Mui-checked": { color: accentColor },
                    mt: -0.5,
                    p: { xs: 1, sm: 1.5 },
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: marketingFonts.body,
                    pt: 1,
                    ...brandedTextSx(accentColor),
                  }}
                >
                  Use my account details
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              required
              variant="outlined"
              label="Full name"
              name="ticket-0-name"
              value={forMe[0] ? user?.name : undefined}
              disabled={forMe[0]}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />
            <TextField
              fullWidth
              required
              variant="outlined"
              type="email"
              label="Email"
              name="ticket-0-email"
              value={forMe[0] ? user?.email : undefined}
              disabled={forMe[0]}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />
            <MuiPhoneNumber
              key={forMe[0] ? "phone-account" : "phone-manual"}
              fullWidth
              required
              variant="outlined"
              label="Mobile"
              name="ticket-0-mob"
              value={forMe[0] ? user?.phoneNumber || "" : undefined}
              disableDropdown={Boolean(forMe[0])}
              InputProps={{ readOnly: Boolean(forMe[0]) }}
              InputLabelProps={{ shrink: true }}
              defaultCountry="eg"
              preferredCountries={["eg", "us"]}
              countryCodeEditable={false}
              disableAreaCodes
              sx={phoneInputSx}
            />
            {event?.moreInfo?.map((info) => (
              <TextField
                key={info}
                fullWidth
                required
                variant="outlined"
                label={info}
                name={`ticket-0-${info}`}
                sx={fieldSx}
              />
            ))}
          </Stack>
        </Section>

        <Section title="Confirm">
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ ...brandedTextSx(accentColor), mb: 0.5, opacity: 0.85 }}>
                Total
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: accentColor,
                  fontSize: { xs: "1.35rem", sm: "1.5rem" },
                }}
              >
                {total} EGP
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ textAlign: { xs: "left", sm: "right" } }}>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting}
                startIcon={<SendIcon />}
                sx={{
                  bgcolor: primaryColor,
                  color: "#fff",
                  minHeight: 48,
                  width: { xs: "100%", sm: "auto" },
                  "&:hover": { bgcolor: primaryColor, filter: "brightness(0.92)" },
                  borderRadius: 2,
                  px: 3,
                }}
              >
                {branding.ctaText || "Register"}
              </Button>
            </Grid>
          </Grid>
        </Section>
      </Stack>
    </Box>
  );
}
